/**
 * Steering YouTube's captions from the player API (#1710).
 *
 * Neither direction can be had from the embed URL alone.
 *
 * Off: `cc_load_policy=0` only means "follow the viewer's own YouTube setting",
 * so a viewer who switched captions on globally still gets them — on top of the
 * burnt-in subtitles some of our videos already carry.
 *
 * On: `cc_load_policy=1` reaches YouTube, but <youtube-video> mirrors YouTube's
 * caption tracklist into its own `textTracks` as soon as playback starts, and
 * since none of those is `showing` it tells YouTube to deselect the lot. That
 * silently defeats the checkbox for every video with real, uploaded subtitles —
 * measured: two runs each on a video with uploaded tracks, captions never
 * appeared with `cc_load_policy=1` alone and always appeared once a track was
 * switched on here. Videos that only have automatic (`asr`) tracks are not
 * affected, because YouTube leaves those out of the tracklist.
 */

/** The slice of the YouTube IFrame API we use. */
export interface CaptionApi {
  setOption?: (module: string, option: string, value: unknown) => void;
}

/** A `TextTrack` as far as we care about it — mirrors one YouTube track. */
export interface CaptionTrack {
  language: string;
  mode: string;
}

/**
 * Delays in ms after playback starts at which we try again. YouTube loads the
 * captions module a moment after the video does, and fills the tracklist later
 * still; anything that arrives before that is silently dropped. We stop after
 * the last one, so whatever the viewer chooses afterwards stands.
 */
export const CAPTION_RETRY_DELAYS = [0, 300, 1000, 2500];

/**
 * Deselects the active caption track. Returns whether the player took the call.
 *
 * Deliberately not `unloadModule('captions')`: unloading strips the CC button,
 * which would leave viewers who rely on captions no way to switch them on at
 * all (WCAG 1.2.2). Deselecting starts the video without captions and keeps the
 * button usable.
 */
export const suppressCaptions = (api?: CaptionApi | null): boolean => {
  if (typeof api?.setOption !== 'function') return false;

  try {
    api.setOption('captions', 'track', {});
    return true;
  } catch {
    // The module may not be loaded yet, or YouTube may have changed the API.
    // Neither is worth breaking playback over.
    return false;
  }
};

/** `de-CH` and `de` are the same language as far as picking a track goes. */
const spokenLanguage = (tag: string) => tag.toLowerCase().split('-')[0];

/**
 * Switches on the caption track that fits the page, and reports whether one is
 * showing afterwards. Setting `mode` is what <youtube-video> listens for; it
 * passes the choice on to YouTube itself.
 */
export const showCaptions = (
  tracks?: Iterable<CaptionTrack> | null,
  preferredLanguage?: string,
): boolean => {
  const available = tracks ? Array.from(tracks) : [];
  if (!available.length) return false;

  // Whatever the viewer picked wins — this runs on a retry ladder, and they may
  // well have changed the track between two rungs.
  if (available.some((track) => track.mode === 'showing')) return true;

  const wanted = preferredLanguage && spokenLanguage(preferredLanguage);
  const track =
    (wanted &&
      available.find((option) => spokenLanguage(option.language) === wanted)) ||
    available[0];

  try {
    track.mode = 'showing';
    return true;
  } catch {
    // The player can go away between the retries. Not worth breaking over.
    return false;
  }
};
